import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL =
  'https://static.readdy.ai/image/5561bd759c3222152be419fe80f35f20/583116c613df7e9b63549d028eae2fd9.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Portfolio', path: '/portfolio' },
  { label: 'Sectors', path: '/sectors' },
  { label: 'Investor Relations', path: '/investor-relations' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen
          ? 'bg-white/95 backdrop-blur-md border-b border-foreground-100/40'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16 md:h-20 gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0 group">
          <img
            src={LOGO_URL}
            alt="CASA CHERE LIMITED"
            className={`h-9 md:h-11 w-auto rounded-md transition-all duration-500 ${
              scrolled || menuOpen ? 'ring-1 ring-foreground-200/60' : 'ring-1 ring-white/15'
            }`}
          />
          <span
            className={`hidden sm:block font-heading font-bold text-[12px] md:text-sm tracking-[0.5px] whitespace-nowrap transition-colors duration-500 ${
              scrolled || menuOpen ? 'text-foreground-950' : 'text-white'
            }`}
          >
            CASA CHERE LIMITED
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-4 xl:gap-6 shrink-0">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[11px] xl:text-xs uppercase tracking-[1.5px] font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? 'text-primary-600'
                  : scrolled
                    ? 'text-foreground-600 hover:text-primary-500'
                    : 'text-white/85 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="ml-1 bg-primary-500 hover:bg-primary-600 text-white text-[11px] xl:text-xs uppercase tracking-[1.5px] font-semibold px-4 xl:px-5 py-2.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Investor Inquiry
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden w-10 h-10 -mr-1 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={`block w-6 h-0.5 rounded-full transition-all duration-300 ${
              scrolled || menuOpen ? 'bg-foreground-900' : 'bg-white'
            } ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}
          />
          <span
            className={`block w-6 h-0.5 rounded-full transition-all duration-300 ${
              scrolled || menuOpen ? 'bg-foreground-900' : 'bg-white'
            } ${menuOpen ? 'opacity-0' : ''}`}
          />
          <span
            className={`block w-6 h-0.5 rounded-full transition-all duration-300 ${
              scrolled || menuOpen ? 'bg-foreground-900' : 'bg-white'
            } ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}
          />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`lg:hidden fixed top-16 md:top-20 left-0 right-0 z-50 bg-white/98 backdrop-blur-md px-5 pb-6 flex flex-col gap-1 border-b border-foreground-100/40 transition-all duration-300 ${
          menuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            onClick={() => setMenuOpen(false)}
            className={`text-sm uppercase tracking-[2px] font-medium py-4 border-b border-foreground-100/50 cursor-pointer ${
              location.pathname === link.path ? 'text-primary-600' : 'text-foreground-700'
            }`}
          >
            {link.label}
          </Link>
        ))}
        <Link
          to="/contact"
          onClick={() => setMenuOpen(false)}
          className="mt-3 bg-primary-500 hover:bg-primary-600 text-white text-sm uppercase tracking-[2px] font-semibold py-3.5 rounded-md text-center cursor-pointer transition-colors"
        >
          Investor Inquiry
        </Link>
      </div>
    </nav>
  );
}